import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Download, Loader2 } from "@digital-twins/design-system/icons";
import {
  CREATIVE_AD_FONTS,
  CREATIVE_AD_PLACEMENTS,
} from "@digital-twins/shared-types";
import type { CreativeAdComposition } from "@digital-twins/shared-types";
import type {
  AdLayoutEditorProps,
  AdLayoutFailureCode,
  AdRenderedExport,
} from "@/types/creative-layouts";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { renderAd, AdLayoutError } from "@/lib/creative-layouts/render";
import {
  readAdSources,
  AdSourceError,
  downloadAdBlob,
} from "@/lib/creative-layouts/export";
import { CreativeUsageWarning } from "@/components/creative-materials/CreativeUsageWarning";
import {
  canAllowExternalUse,
  creativeUsageWarningLevel,
} from "@/components/creative-materials/creativeUsage";
import {
  CreativeEditorDialog,
  CreativeEditorSection,
} from "./CreativeEditorDialog";

export function AdLayoutEditor({
  source,
  logos,
  onClose,
}: AdLayoutEditorProps) {
  const { t } = useTranslation("app");
  const [composition, setComposition] = useState<CreativeAdComposition>({
    schemaVersion: 1,
    placementId: "meta-feed-square",
    template: "split",
    templateVersion: 1,
    source: { token: source.token, revision: source.revision },
    logo: null,
    headline: "",
    body: "",
    cta: "",
    fontId: "inter",
    background: "#ffffff",
    foreground: "#171717",
    fit: "contain",
    mimeType: "image/png",
    jpegQuality: 0.85,
    intendedUse: "internal",
  });
  const [result, setResult] = useState<AdRenderedExport | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AdLayoutFailureCode | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: "nearest" });
  }, [error]);
  const placement = CREATIVE_AD_PLACEMENTS.find(
    (item) => item.id === composition.placementId,
  )!;
  const selectedLogo = logos.find(
    (logo) => logo.token === composition.logo?.token,
  );
  const usageWarnings =
    composition.intendedUse === "external"
      ? [source, ...(selectedLogo ? [selectedLogo] : [])].flatMap((item) => {
          const usage = creativeUsageWarningLevel(item);
          return usage ? [{ item, usage }] : [];
        })
      : [];
  useEffect(() => {
    if (!result) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(result.blob);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [result]);
  function change<K extends keyof CreativeAdComposition>(
    key: K,
    value: CreativeAdComposition[K],
  ) {
    setComposition((current) => ({ ...current, [key]: value }));
    setResult(null);
    setError(null);
  }
  async function generatePreview() {
    setBusy(true);
    setError(null);
    setResult(null);
    const frozen = structuredClone(composition);
    try {
      setResult(
        await renderAd({
          composition: frozen,
          sources: await readAdSources(frozen),
        }),
      );
    } catch (failure) {
      setError(
        failure instanceof AdLayoutError || failure instanceof AdSourceError
          ? failure.code
          : "source",
      );
    } finally {
      setBusy(false);
    }
  }
  async function download(kind: "image" | "manifest") {
    if (!result) return;
    setBusy(true);
    setError(null);
    try {
      await readAdSources(result.manifest.composition);
      const stem = `${result.manifest.placement.id}-${result.manifest.output.width}x${result.manifest.output.height}`;
      if (kind === "image")
        downloadAdBlob(
          result.blob,
          `${stem}.${result.blob.type === "image/png" ? "png" : "jpg"}`,
        );
      else
        downloadAdBlob(
          new Blob([JSON.stringify(result.manifest, null, 2)], {
            type: "application/json",
          }),
          `${stem}.json`,
        );
    } catch (failure) {
      setResult(null);
      setError(failure instanceof AdSourceError ? failure.code : "source");
    } finally {
      setBusy(false);
    }
  }
  return (
    <CreativeEditorDialog
      open
      busy={busy}
      kind="creatives"
      title={t("adLayouts.title")}
      description={source.title}
      onClose={onClose}
      testId="ad-layout-editor"
      footer={
        <>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            {t("creativeLibrary.done")}
          </Button>
          <Button
            variant="outline"
            disabled={busy || !result}
            onClick={() => void download("manifest")}
            data-testid="ad-layout-manifest"
          >
            {t("adLayouts.manifest")}
          </Button>
          <Button
            variant="primary"
            disabled={busy || !result}
            onClick={() => void download("image")}
            data-testid="ad-layout-download"
          >
            <Download className="h-4 w-4" />
            {t("adLayouts.download")}
          </Button>
        </>
      }
    >
      <fieldset disabled={busy} className="flex min-w-0 flex-col gap-4">
        <CreativeEditorSection>
          <Label>{t("adLayouts.placement")}</Label>
          <Select
            value={composition.placementId}
            disabled={busy}
            onValueChange={(value) => {
              const next = CREATIVE_AD_PLACEMENTS.find(
                (item) => item.id === value,
              )!;
              setComposition((current) => ({
                ...current,
                placementId: value,
                mimeType:
                  next.platform === "google" ? "image/jpeg" : "image/png",
              }));
              setResult(null);
            }}
          >
            <SelectTrigger
              aria-label={t("adLayouts.placement")}
              data-testid="ad-layout-placement"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CREATIVE_AD_PLACEMENTS.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name} · {item.width} × {item.height}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Text size="xs" color="muted">
            {placement.width} × {placement.height} px
            {placement.maximumBytes
              ? ` · ${placement.maximumBytes.toLocaleString()} bytes`
              : ""}
          </Text>
          <Text size="xs" color="muted">
            {t(
              placement.verification === "export_preset_unverified"
                ? "adLayouts.metaUnverified"
                : "adLayouts.verified",
            )}
          </Text>
          <div className="flex flex-wrap gap-2">
            {placement.sources.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary underline"
              >
                {t("adLayouts.specSource")}
              </a>
            ))}
          </div>
          <Text size="xs" color="muted">
            {t("adLayouts.paddingNote")}
          </Text>
        </CreativeEditorSection>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label>{t("adLayouts.template")}</Label>
            <Select
              value={composition.template}
              disabled={busy}
              onValueChange={(value) =>
                change("template", value as "split" | "footer")
              }
            >
              <SelectTrigger aria-label={t("adLayouts.template")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="split">{t("adLayouts.split")}</SelectItem>
                <SelectItem value="footer">{t("adLayouts.footer")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label>{t("adLayouts.font")}</Label>
            <Select
              value={composition.fontId}
              disabled={busy}
              onValueChange={(value) =>
                change("fontId", value as CreativeAdComposition["fontId"])
              }
            >
              <SelectTrigger aria-label={t("adLayouts.font")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CREATIVE_AD_FONTS.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.family.replace("Ad ", "")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Text size="xs" color="muted">
          {t("adLayouts.fontHint")}
        </Text>
        {(["headline", "body", "cta"] as const).map((key) => (
          <div key={key} className="flex flex-col gap-2">
            <Label htmlFor={`ad-${key}`}>{t(`adLayouts.${key}`)}</Label>
            <Textarea
              id={`ad-${key}`}
              value={composition[key]}
              maxLength={key === "body" ? 800 : 200}
              rows={2}
              onChange={(event) => change(key, event.target.value)}
            />
          </div>
        ))}
        <Text size="xs" color="muted">
          {t("adLayouts.copyHint")}
        </Text>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label>{t("adLayouts.logo")}</Label>
            <Select
              disabled={busy}
              value={composition.logo?.token ?? "none"}
              onValueChange={(value) => {
                const logo = logos.find((item) => item.token === value);
                change(
                  "logo",
                  logo ? { token: logo.token, revision: logo.revision } : null,
                );
              }}
            >
              <SelectTrigger aria-label={t("adLayouts.logo")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("adLayouts.noLogo")}</SelectItem>
                {logos.map((logo) => (
                  <SelectItem key={logo.token} value={logo.token}>
                    {logo.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label>{t("adLayouts.fit")}</Label>
            <Select
              disabled={busy}
              value={composition.fit}
              onValueChange={(value) =>
                change("fit", value as "contain" | "cover")
              }
            >
              <SelectTrigger aria-label={t("adLayouts.fit")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contain">
                  {t("adLayouts.contain")}
                </SelectItem>
                <SelectItem value="cover">{t("adLayouts.cover")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(["background", "foreground"] as const).map((key) => (
            <div key={key} className="flex flex-col gap-2">
              <Label htmlFor={`ad-${key}`}>{t(`adLayouts.${key}`)}</Label>
              <Input
                id={`ad-${key}`}
                type="color"
                value={composition[key]}
                onChange={(event) => change(key, event.target.value)}
              />
            </div>
          ))}
          <div className="flex flex-col gap-2">
            <Label>{t("adLayouts.format")}</Label>
            <Select
              disabled={busy}
              value={composition.mimeType}
              onValueChange={(value) =>
                change("mimeType", value as "image/png" | "image/jpeg")
              }
            >
              <SelectTrigger aria-label={t("adLayouts.format")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="image/png">PNG</SelectItem>
                <SelectItem value="image/jpeg">JPEG</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label>{t("creativeLibrary.intendedUsage")}</Label>
            <Select
              disabled={busy}
              value={composition.intendedUse}
              onValueChange={(value) =>
                change("intendedUse", value as "internal" | "external")
              }
            >
              <SelectTrigger aria-label={t("creativeLibrary.intendedUsage")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">
                  {t("creativeLibrary.contextInternal")}
                </SelectItem>
                <SelectItem value="external">
                  {t("creativeLibrary.contextExternal")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {usageWarnings.map(({ item, usage }) => (
          <CreativeUsageWarning
            key={item.token}
            name={item.title}
            usage={usage}
            allowedUseToken={canAllowExternalUse(item) ? item.token : null}
            testId={`ad-layout-usage-warning-${item.token}`}
          />
        ))}
        {composition.mimeType === "image/jpeg" && (
          <div className="flex flex-col gap-2">
            <Label htmlFor="ad-quality">
              {t("adLayouts.quality")} ·{" "}
              {Math.round(composition.jpegQuality * 100)}%
            </Label>
            <Input
              id="ad-quality"
              type="range"
              min={10}
              max={100}
              value={composition.jpegQuality * 100}
              onChange={(event) =>
                change("jpegQuality", Number(event.target.value) / 100)
              }
            />
          </div>
        )}
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => void generatePreview()}
          data-testid="ad-layout-preview"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("adLayouts.preview")}
        </Button>
      </fieldset>
      {error && (
        <div ref={errorRef} role="alert" className="flex items-start gap-2 text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <Text size="sm" className="text-destructive">
            {t(`adLayouts.errors.${error}`)}
          </Text>
        </div>
      )}
      {preview && (
        <div className="flex flex-col gap-2">
          <img
            src={preview}
            alt={t("adLayouts.preview")}
            className="max-h-96 w-full object-contain"
            data-testid="ad-layout-preview-image"
          />
          <Text size="xs" color="muted">
            {result?.manifest.output.bytes.toLocaleString()} bytes ·{" "}
            {t("adLayouts.localExport")}
          </Text>
        </div>
      )}
    </CreativeEditorDialog>
  );
}
